import { API_BASE, graphql } from './graphql.js';

// Typed query/mutation wrappers for the P2P stream. One-file-per-stream keeps
// the API surface easy to navigate as more streams come online.

export type VendorState =
  | 'PROSPECT'
  | 'ONBOARDING'
  | 'ACTIVE'
  | 'SUSPENDED'
  | 'RETIRED'
  | 'MERGED';

export type PaymentTerms =
  | 'NET_15'
  | 'NET_30'
  | 'NET_45'
  | 'NET_60'
  | 'NET_90'
  | 'TWO_TEN_NET_30'
  | 'DUE_ON_RECEIPT';

export type PaymentMethod = 'ACH' | 'CHECK' | 'WIRE' | 'VIRTUAL_CARD';

export interface VendorRow {
  readonly vendorRef: string;
  readonly legalName: string;
  readonly displayName: string;
  readonly state: VendorState;
  readonly opcoRef: string;
  readonly city: string;
  readonly region: string;
  readonly country: string;
  readonly createdAt: string;
  readonly paymentProfile: {
    readonly paymentTerms: PaymentTerms;
    readonly paymentMethod: PaymentMethod;
    readonly defaultGLAccount: string;
  } | null;
}

const VENDORS_QUERY = `
  query Vendors($opcoRef: ID!) {
    vendors(opcoRef: $opcoRef) {
      vendorRef
      legalName
      displayName
      state
      opcoRef
      city
      region
      country
      createdAt
      paymentProfile {
        paymentTerms
        paymentMethod
        defaultGLAccount
      }
    }
  }
`;

export async function fetchVendors(opcoRef: string): Promise<ReadonlyArray<VendorRow>> {
  const { vendors } = await graphql<{ vendors: VendorRow[] }>(VENDORS_QUERY, { opcoRef });
  return vendors;
}

export interface VendorDetail {
  readonly vendorRef: string;
  readonly legalName: string;
  readonly displayName: string;
  readonly taxId: string;
  readonly addressLine1: string;
  readonly addressLine2: string | null;
  readonly city: string;
  readonly region: string;
  readonly postalCode: string;
  readonly country: string;
  readonly contactEmail: string | null;
  readonly contactPhone: string | null;
  readonly state: VendorState;
  readonly opcoRef: string;
  readonly createdAt: string;
  readonly paymentProfile: {
    readonly profileRef: string;
    readonly paymentTerms: PaymentTerms;
    readonly paymentMethod: PaymentMethod;
    readonly defaultGLAccount: string;
    readonly form1099Status: { kind: Form1099Kind; formType?: string };
    readonly taxExemptionStatus: { kind: TaxExemptionKind; certificateRef?: string };
    readonly paymentHoldFlags: ReadonlyArray<string>;
    readonly apHoldFlags: ReadonlyArray<string>;
    readonly createdAt: string;
  } | null;
}

const VENDOR_QUERY = `
  query Vendor($ref: ID!) {
    vendor(ref: $ref) {
      vendorRef
      legalName
      displayName
      taxId
      addressLine1
      addressLine2
      city
      region
      postalCode
      country
      contactEmail
      contactPhone
      state
      opcoRef
      createdAt
      paymentProfile {
        profileRef
        paymentTerms
        paymentMethod
        defaultGLAccount
        form1099Status
        taxExemptionStatus
        paymentHoldFlags
        apHoldFlags
        createdAt
      }
    }
  }
`;

export async function fetchVendor(ref: string): Promise<VendorDetail | null> {
  const { vendor } = await graphql<{ vendor: VendorDetail | null }>(VENDOR_QUERY, { ref });
  return vendor;
}

export interface UpdateVendorStateInput {
  readonly vendorRef: string;
  readonly toState: VendorState;
  readonly reason?: string;
  readonly requestingUser: string;
}

export type UpdateVendorStateResult =
  | {
      readonly __typename: 'UpdateVendorStateSuccess';
      readonly vendorRef: string;
      readonly fromState: VendorState;
      readonly toState: VendorState;
    }
  | {
      readonly __typename: 'UpdateVendorStateFailure';
      readonly class: string;
      readonly details: unknown;
    };

const UPDATE_VENDOR_STATE_MUTATION = `
  mutation Upd($input: UpdateVendorStateInput!) {
    updateVendorState(input: $input) {
      __typename
      ... on UpdateVendorStateSuccess { vendorRef fromState toState }
      ... on UpdateVendorStateFailure { class details }
    }
  }
`;

export async function updateVendorState(input: UpdateVendorStateInput): Promise<UpdateVendorStateResult> {
  const { updateVendorState: result } = await graphql<{
    updateVendorState: UpdateVendorStateResult;
  }>(UPDATE_VENDOR_STATE_MUTATION, { input });
  return result;
}

// UpdateVendor — edit pattern. The dialog builds this from the current
// vendor + the user's diff; `clear` is the list of nullable string fields
// the user explicitly cleared (vs left unchanged).
export interface UpdateVendorInput {
  readonly vendorRef: string;
  readonly displayName?: string;
  readonly taxId?: string;
  readonly addressLine1?: string;
  readonly addressLine2?: string;
  readonly city?: string;
  readonly region?: string;
  readonly postalCode?: string;
  readonly country?: string;
  readonly contactEmail?: string;
  readonly contactPhone?: string;
  readonly paymentTerms?: PaymentTerms;
  readonly paymentMethod?: PaymentMethod;
  readonly defaultGLAccount?: string;
  readonly form1099Status?: { kind: Form1099Kind; formType?: Form1099FormType };
  readonly taxExemptionStatus?: { kind: TaxExemptionKind; certificateRef?: string };
  readonly clear?: ReadonlyArray<'addressLine2' | 'contactEmail' | 'contactPhone'>;
  readonly requestingUser: string;
}

export type UpdateVendorResult =
  | {
      readonly __typename: 'UpdateVendorSuccess';
      readonly vendorRef: string;
      readonly changedFields: ReadonlyArray<string>;
    }
  | {
      readonly __typename: 'UpdateVendorFailure';
      readonly class: string;
      readonly details: unknown;
    };

const UPDATE_VENDOR_MUTATION = `
  mutation UpdV($input: UpdateVendorInput!) {
    updateVendor(input: $input) {
      __typename
      ... on UpdateVendorSuccess { vendorRef changedFields }
      ... on UpdateVendorFailure { class details }
    }
  }
`;

export async function updateVendor(input: UpdateVendorInput): Promise<UpdateVendorResult> {
  const { updateVendor: result } = await graphql<{ updateVendor: UpdateVendorResult }>(
    UPDATE_VENDOR_MUTATION,
    { input },
  );
  return result;
}

// UpdateRequisition / UpdatePurchaseOrder / UpdateAPInvoice — thin edits
// per the edit-pattern sweep. Each follows the same shape as updateVendor:
// optional fields, `clear` list for nullable string clears, Success/Failure
// union.
export interface UpdateRequisitionInput {
  readonly requisitionRef: string;
  readonly justification?: string;
  readonly suggestedVendorRef?: string;
  readonly notes?: string;
  readonly clear?: ReadonlyArray<'justification' | 'suggestedVendorRef' | 'notes'>;
  readonly requestingUser: string;
}

export type UpdateRequisitionResult =
  | {
      readonly __typename: 'UpdateRequisitionSuccess';
      readonly requisitionRef: string;
      readonly changedFields: ReadonlyArray<string>;
    }
  | {
      readonly __typename: 'UpdateRequisitionFailure';
      readonly class: string;
      readonly details: unknown;
    };

const UPDATE_REQUISITION_MUTATION = `
  mutation UpdR($input: UpdateRequisitionInput!) {
    updateRequisition(input: $input) {
      __typename
      ... on UpdateRequisitionSuccess { requisitionRef changedFields }
      ... on UpdateRequisitionFailure { class details }
    }
  }
`;

export async function updateRequisition(
  input: UpdateRequisitionInput,
): Promise<UpdateRequisitionResult> {
  const { updateRequisition: result } = await graphql<{
    updateRequisition: UpdateRequisitionResult;
  }>(UPDATE_REQUISITION_MUTATION, { input });
  return result;
}

export interface UpdatePurchaseOrderInput {
  readonly poRef: string;
  readonly notes?: string;
  readonly clear?: ReadonlyArray<'notes'>;
  readonly requestingUser: string;
}

export type UpdatePurchaseOrderResult =
  | {
      readonly __typename: 'UpdatePurchaseOrderSuccess';
      readonly poRef: string;
      readonly changedFields: ReadonlyArray<string>;
    }
  | {
      readonly __typename: 'UpdatePurchaseOrderFailure';
      readonly class: string;
      readonly details: unknown;
    };

const UPDATE_PURCHASE_ORDER_MUTATION = `
  mutation UpdPO($input: UpdatePurchaseOrderInput!) {
    updatePurchaseOrder(input: $input) {
      __typename
      ... on UpdatePurchaseOrderSuccess { poRef changedFields }
      ... on UpdatePurchaseOrderFailure { class details }
    }
  }
`;

export async function updatePurchaseOrder(
  input: UpdatePurchaseOrderInput,
): Promise<UpdatePurchaseOrderResult> {
  const { updatePurchaseOrder: result } = await graphql<{
    updatePurchaseOrder: UpdatePurchaseOrderResult;
  }>(UPDATE_PURCHASE_ORDER_MUTATION, { input });
  return result;
}

export interface UpdateAPInvoiceInput {
  readonly invoiceRef: string;
  readonly notes?: string;
  readonly clear?: ReadonlyArray<'notes'>;
  readonly requestingUser: string;
}

export type UpdateAPInvoiceResult =
  | {
      readonly __typename: 'UpdateAPInvoiceSuccess';
      readonly invoiceRef: string;
      readonly changedFields: ReadonlyArray<string>;
    }
  | {
      readonly __typename: 'UpdateAPInvoiceFailure';
      readonly class: string;
      readonly details: unknown;
    };

const UPDATE_AP_INVOICE_MUTATION = `
  mutation UpdInv($input: UpdateAPInvoiceInput!) {
    updateApInvoice(input: $input) {
      __typename
      ... on UpdateAPInvoiceSuccess { invoiceRef changedFields }
      ... on UpdateAPInvoiceFailure { class details }
    }
  }
`;

export async function updateApInvoice(
  input: UpdateAPInvoiceInput,
): Promise<UpdateAPInvoiceResult> {
  const { updateApInvoice: result } = await graphql<{
    updateApInvoice: UpdateAPInvoiceResult;
  }>(UPDATE_AP_INVOICE_MUTATION, { input });
  return result;
}

// Client-side state-machine mirror for showing action buttons. The server
// remains the authority; UI just hides buttons that would fail. Keep in
// sync with packages/streams/p2p/src/domain/vendor-state-machine.ts.
const TRANSITIONS: Readonly<Record<VendorState, ReadonlyArray<VendorState>>> = {
  PROSPECT: ['ONBOARDING', 'RETIRED'],
  ONBOARDING: ['ACTIVE', 'RETIRED'],
  ACTIVE: ['SUSPENDED', 'RETIRED', 'MERGED'],
  SUSPENDED: ['ACTIVE', 'RETIRED', 'MERGED'],
  RETIRED: [],
  MERGED: [],
};

export function legalNextStates(from: VendorState): ReadonlyArray<VendorState> {
  return TRANSITIONS[from];
}

export type Form1099Kind = 'NOT_REQUIRED' | 'REQUIRED';
export type Form1099FormType =
  | 'FORM_1099_MISC'
  | 'FORM_1099_NEC'
  | 'FORM_1099_INT'
  | 'FORM_1099_DIV';
export type TaxExemptionKind = 'TAXABLE' | 'EXEMPT';

export interface Form1099StatusInput {
  readonly kind: Form1099Kind;
  readonly formType?: Form1099FormType | null;
}

export interface TaxExemptionStatusInput {
  readonly kind: TaxExemptionKind;
  readonly certificateRef?: string | null;
}

export interface CreateVendorInput {
  readonly legalName: string;
  readonly displayName: string;
  readonly taxId: string;
  readonly addressLine1: string;
  readonly addressLine2?: string;
  readonly city: string;
  readonly region: string;
  readonly postalCode: string;
  readonly country: string;
  readonly contactEmail?: string;
  readonly contactPhone?: string;
  readonly paymentTerms: PaymentTerms;
  readonly paymentMethod: PaymentMethod;
  readonly defaultGLAccount: string;
  readonly form1099Status: Form1099StatusInput;
  readonly taxExemptionStatus: TaxExemptionStatusInput;
  readonly opcoRef: string;
  readonly requestingUser: string;
}

export type CreateVendorResult =
  | {
      readonly __typename: 'OnboardVendorSuccess';
      readonly vendorRef: string;
      readonly profileRef: string;
    }
  | {
      readonly __typename: 'OnboardVendorFailure';
      readonly class: string;
      readonly details: unknown;
    };

const CREATE_VENDOR_MUTATION = `
  mutation Create($input: OnboardVendorInput!) {
    createVendor(input: $input) {
      __typename
      ... on OnboardVendorSuccess {
        vendorRef
        profileRef
      }
      ... on OnboardVendorFailure {
        class
        details
      }
    }
  }
`;

export async function createVendor(input: CreateVendorInput): Promise<CreateVendorResult> {
  const { createVendor: result } = await graphql<{ createVendor: CreateVendorResult }>(
    CREATE_VENDOR_MUTATION,
    { input },
  );
  return result;
}

// ---------------------------------------------------------------------------
// Purchase orders
// ---------------------------------------------------------------------------

export type PurchaseOrderState =
  | 'DRAFT'
  | 'APPROVED'
  | 'TRANSMITTED'
  | 'ACKNOWLEDGED'
  | 'OPEN'
  | 'PARTIALLY_RECEIVED'
  | 'FULLY_RECEIVED'
  | 'CLOSED'
  | 'CANCELLED';

export type PricingSource =
  | 'CONTRACT'
  | 'QUOTE'
  | 'STANDARD_PRICE_LIST'
  | 'NEGOTIATED';

export interface PurchaseOrderLineRow {
  readonly lineRef: string;
  readonly poRef: string;
  readonly lineNumber: number;
  readonly description: string;
  readonly unitOfMeasure: string;
  readonly quantity: string;
  readonly unitPrice: string;
  readonly lineTotal: string;
  readonly defaultGLAccount: string;
  readonly pricingSource: PricingSource;
  readonly sourceRequisitionLineRef: string | null;
  readonly receivedToDate: string;
}

export interface PurchaseOrderRow {
  readonly poRef: string;
  readonly vendorRef: string;
  readonly opcoRef: string;
  readonly state: PurchaseOrderState;
  readonly currencyCode: string;
  readonly subtotal: string;
  readonly grandTotal: string;
  readonly notes: string | null;
  readonly createdAt: string;
  readonly createdBy: string;
  readonly reissueOfPoRef: string | null;
  readonly vendor: { readonly legalName: string; readonly displayName: string } | null;
  readonly lines: ReadonlyArray<PurchaseOrderLineRow>;
}

// Shared GraphQL fragment-equivalent: PO line fields. Kept as a string here
// rather than a real fragment because the rest of the client doesn't use
// graphql-tag / Apollo. Inlined into every PO query that needs lines.
const PO_LINE_FIELDS = `
  lineRef
  poRef
  lineNumber
  description
  unitOfMeasure
  quantity
  unitPrice
  lineTotal
  defaultGLAccount
  pricingSource
  sourceRequisitionLineRef
  receivedToDate
`;

const PURCHASE_ORDERS_QUERY = `
  query POs($opcoRef: ID!) {
    purchaseOrders(opcoRef: $opcoRef) {
      poRef
      vendorRef
      opcoRef
      state
      currencyCode
      subtotal
      grandTotal
      notes
      createdAt
      createdBy
      reissueOfPoRef
      vendor { legalName displayName }
      lines { ${PO_LINE_FIELDS} }
    }
  }
`;

export async function fetchPurchaseOrders(
  opcoRef: string,
): Promise<ReadonlyArray<PurchaseOrderRow>> {
  const { purchaseOrders } = await graphql<{ purchaseOrders: PurchaseOrderRow[] }>(
    PURCHASE_ORDERS_QUERY,
    { opcoRef },
  );
  return purchaseOrders;
}

const PURCHASE_ORDER_QUERY = `
  query PO($ref: ID!) {
    purchaseOrder(ref: $ref) {
      poRef
      vendorRef
      opcoRef
      state
      currencyCode
      subtotal
      grandTotal
      notes
      createdAt
      createdBy
      reissueOfPoRef
      vendor { legalName displayName }
      lines { ${PO_LINE_FIELDS} }
    }
  }
`;

export async function fetchPurchaseOrder(ref: string): Promise<PurchaseOrderRow | null> {
  const { purchaseOrder } = await graphql<{ purchaseOrder: PurchaseOrderRow | null }>(
    PURCHASE_ORDER_QUERY,
    { ref },
  );
  return purchaseOrder;
}

export interface CreatePurchaseOrderLineInput {
  readonly description: string;
  readonly unitOfMeasure: string;
  readonly quantity: string;
  readonly unitPrice: string;
  readonly defaultGLAccount: string;
  readonly pricingSource: PricingSource;
  readonly sourceRequisitionLineRef?: string;
}

export interface CreatePurchaseOrderInput {
  readonly vendorRef: string;
  readonly opcoRef: string;
  readonly currencyCode: string;
  readonly lines: ReadonlyArray<CreatePurchaseOrderLineInput>;
  readonly notes?: string;
  readonly requestingUser: string;
}

export type CreatePurchaseOrderResult =
  | {
      readonly __typename: 'CreatePurchaseOrderSuccess';
      readonly poRef: string;
      readonly lineRefs: ReadonlyArray<string>;
    }
  | {
      readonly __typename: 'CreatePurchaseOrderFailure';
      readonly class: string;
      readonly details: unknown;
    };

const CREATE_PURCHASE_ORDER_MUTATION = `
  mutation CreatePO($input: CreatePurchaseOrderInput!) {
    createPurchaseOrder(input: $input) {
      __typename
      ... on CreatePurchaseOrderSuccess { poRef lineRefs }
      ... on CreatePurchaseOrderFailure { class details }
    }
  }
`;

export async function createPurchaseOrder(
  input: CreatePurchaseOrderInput,
): Promise<CreatePurchaseOrderResult> {
  const { createPurchaseOrder: result } = await graphql<{
    createPurchaseOrder: CreatePurchaseOrderResult;
  }>(CREATE_PURCHASE_ORDER_MUTATION, { input });
  return result;
}

// ---------------------------------------------------------------------------
// Receipts
// ---------------------------------------------------------------------------

export type ReceiptState = 'POSTED';

export interface ReceiptLineRow {
  readonly receiptLineRef: string;
  readonly receiptRef: string;
  readonly poLineRef: string;
  readonly receivedQuantity: string;
}

export type ReceiptKind = 'GOODS' | 'SERVICE';

export interface ReceiptAttachment {
  readonly ref: string;
  readonly filename: string;
  readonly mimeType?: string;
  readonly sizeBytes?: number;
  readonly uploadedAt: string;
}

// Uploads a file via the Worker's R2-backed REST endpoint. Returns the
// ReceiptAttachment shape that the recordGoodsReceipt mutation expects.
// Errors carry the server's structured `error` / `detail` fields.
export class UploadError extends Error {
  constructor(
    message: string,
    public readonly code: string,
    public readonly detail?: string,
  ) {
    super(message);
    this.name = 'UploadError';
  }
}

// Turns an r2://anvil-uploads/<key> ref into an HTTP URL the browser can
// fetch via the Worker download endpoint. Refs from other buckets (or
// missing the expected prefix) return null so the UI can render a
// non-clickable label instead of a broken link.
export function receiptAttachmentDownloadUrl(ref: string): string | null {
  const prefix = 'r2://anvil-uploads/';
  if (!ref.startsWith(prefix)) return null;
  const key = ref.slice(prefix.length);
  if (!key.startsWith('receipts/')) return null;
  // The Worker accepts the unescaped key path. Encode each segment so
  // unusual filename characters (already sanitised at upload time but
  // belt-and-braces) survive the URL boundary.
  const encoded = key
    .split('/')
    .map((seg) => encodeURIComponent(seg))
    .join('/');
  return `${API_BASE}/api/v1/uploads/receipt-attachment/${encoded}`;
}

export async function uploadReceiptAttachment(file: File): Promise<ReceiptAttachment> {
  const form = new FormData();
  form.set('file', file, file.name);
  const res = await fetch(`${API_BASE}/api/v1/uploads/receipt-attachment`, {
    method: 'POST',
    body: form,
  });
  if (!res.ok) {
    let code = `HTTP_${res.status}`;
    let detail: string | undefined;
    try {
      const body = (await res.json()) as { error?: string; detail?: string };
      if (body.error) code = body.error;
      if (body.detail) detail = body.detail;
    } catch {
      // Non-JSON body — keep the HTTP code as the failure class.
    }
    throw new UploadError(
      `Upload failed (${code}${detail ? `: ${detail}` : ''})`,
      code,
      detail,
    );
  }
  return (await res.json()) as ReceiptAttachment;
}

export interface ReceiptRow {
  readonly receiptRef: string;
  readonly poRef: string;
  readonly opcoRef: string;
  readonly state: ReceiptState;
  readonly kind: ReceiptKind;
  readonly attachments: ReadonlyArray<ReceiptAttachment>;
  readonly receivedAt: string;
  readonly receivedBy: string;
  readonly notes: string | null;
  readonly createdAt: string;
  readonly reversedAt: string | null;
  readonly reversedBy: string | null;
  readonly reversalReason: string | null;
  readonly lines: ReadonlyArray<ReceiptLineRow>;
}

const RECEIPTS_FOR_PO_QUERY = `
  query Rs($poRef: ID!) {
    receiptsForPo(poRef: $poRef) {
      receiptRef
      poRef
      opcoRef
      state
      kind
      attachments
      receivedAt
      receivedBy
      notes
      createdAt
      reversedAt
      reversedBy
      reversalReason
      lines {
        receiptLineRef
        receiptRef
        poLineRef
        receivedQuantity
      }
    }
  }
`;

export async function fetchReceiptsForPo(
  poRef: string,
): Promise<ReadonlyArray<ReceiptRow>> {
  const { receiptsForPo } = await graphql<{ receiptsForPo: ReceiptRow[] }>(
    RECEIPTS_FOR_PO_QUERY,
    { poRef },
  );
  return receiptsForPo;
}

const RECEIPTS_BY_OPCO_QUERY = `
  query ROpco($opcoRef: ID!) {
    receipts(opcoRef: $opcoRef) {
      receiptRef
      poRef
      opcoRef
      state
      kind
      attachments
      receivedAt
      receivedBy
      notes
      createdAt
      reversedAt
      reversedBy
      reversalReason
      lines {
        receiptLineRef
        receiptRef
        poLineRef
        receivedQuantity
      }
    }
  }
`;

export async function fetchReceipts(
  opcoRef: string,
): Promise<ReadonlyArray<ReceiptRow>> {
  const { receipts } = await graphql<{ receipts: ReceiptRow[] }>(
    RECEIPTS_BY_OPCO_QUERY,
    { opcoRef },
  );
  return receipts;
}

const RECEIPT_QUERY = `
  query R($ref: ID!) {
    receipt(ref: $ref) {
      receiptRef
      poRef
      opcoRef
      state
      kind
      attachments
      receivedAt
      receivedBy
      notes
      createdAt
      reversedAt
      reversedBy
      reversalReason
      lines {
        receiptLineRef
        receiptRef
        poLineRef
        receivedQuantity
      }
    }
  }
`;

export async function fetchReceipt(ref: string): Promise<ReceiptRow | null> {
  const { receipt } = await graphql<{ receipt: ReceiptRow | null }>(
    RECEIPT_QUERY,
    { ref },
  );
  return receipt;
}

export interface RecordGoodsReceiptLineInput {
  readonly poLineRef: string;
  readonly receivedQuantity: string;
}

export interface RecordGoodsReceiptInput {
  readonly poRef: string;
  readonly lines: ReadonlyArray<RecordGoodsReceiptLineInput>;
  readonly notes?: string;
  readonly receivingUser: string;
  readonly kind?: ReceiptKind;
  readonly attachments?: ReadonlyArray<ReceiptAttachment>;
}

export type RecordGoodsReceiptResult =
  | {
      readonly __typename: 'RecordGoodsReceiptSuccess';
      readonly receiptRef: string;
      readonly receiptLineRefs: ReadonlyArray<string>;
      readonly newPoState: PurchaseOrderState;
    }
  | {
      readonly __typename: 'RecordGoodsReceiptFailure';
      readonly class: string;
      readonly details: unknown;
    };

const RECORD_GOODS_RECEIPT_MUTATION = `
  mutation RecordR($input: RecordGoodsReceiptInput!) {
    recordGoodsReceipt(input: $input) {
      __typename
      ... on RecordGoodsReceiptSuccess { receiptRef receiptLineRefs newPoState }
      ... on RecordGoodsReceiptFailure { class details }
    }
  }
`;

export async function recordGoodsReceipt(
  input: RecordGoodsReceiptInput,
): Promise<RecordGoodsReceiptResult> {
  const { recordGoodsReceipt: result } = await graphql<{
    recordGoodsReceipt: RecordGoodsReceiptResult;
  }>(RECORD_GOODS_RECEIPT_MUTATION, { input });
  return result;
}

// ---------------------------------------------------------------------------
// AP invoices
// ---------------------------------------------------------------------------

export type APInvoiceState =
  | 'RECEIVED'
  | 'MATCHED'
  | 'VARIANCE_REVIEW'
  | 'APPROVED'
  | 'ON_HOLD'
  | 'REJECTED'
  | 'CANCELLED';

export type APInvoiceKind = 'PO_MATCHED' | 'NON_PO' | 'CREDIT_NOTE';

export type APInvoiceIntakeChannel =
  | 'EMAIL'
  | 'VENDOR_PORTAL'
  | 'MANUAL_ENTRY'
  | 'EDI'
  | 'AI_EXTRACTION';

export type APInvoiceLineVariance =
  | { readonly kind: 'NONE' }
  | { readonly kind: 'QTY_OVER'; readonly invoiceQty: string; readonly receivedQty: string }
  | { readonly kind: 'PRICE_DIFF'; readonly invoicePrice: string; readonly poPrice: string }
  | {
      readonly kind: 'QTY_OVER_AND_PRICE_DIFF';
      readonly invoiceQty: string;
      readonly receivedQty: string;
      readonly invoicePrice: string;
      readonly poPrice: string;
    };

export interface APInvoiceLineRow {
  readonly invoiceLineRef: string;
  readonly invoiceRef: string;
  readonly lineNumber: number;
  // Null for NON_PO invoice lines.
  readonly poLineRef: string | null;
  readonly description: string;
  readonly quantity: string;
  readonly unitPrice: string;
  readonly lineTotal: string;
  // Required for NON_PO lines; null for PO_MATCHED (GL flows from the PO).
  readonly glAccount: string | null;
  readonly variance: APInvoiceLineVariance;
}

export interface APInvoiceRow {
  readonly invoiceRef: string;
  readonly vendorRef: string;
  readonly opcoRef: string;
  readonly invoiceNumber: string;
  readonly invoiceDate: string;
  readonly currencyCode: string;
  readonly subtotal: string;
  readonly total: string;
  readonly state: APInvoiceState;
  readonly kind: APInvoiceKind;
  readonly sourceInvoiceRef: string | null;
  readonly intakeChannel: APInvoiceIntakeChannel;
  readonly notes: string | null;
  readonly createdAt: string;
  readonly createdBy: string;
  readonly vendor: { readonly legalName: string; readonly displayName: string } | null;
  readonly lines: ReadonlyArray<APInvoiceLineRow>;
}

const AP_INVOICE_LINE_FIELDS = `
  invoiceLineRef
  invoiceRef
  lineNumber
  poLineRef
  description
  quantity
  unitPrice
  lineTotal
  glAccount
  variance
`;

const AP_INVOICE_FIELDS = `
  invoiceRef
  vendorRef
  opcoRef
  invoiceNumber
  invoiceDate
  currencyCode
  subtotal
  total
  state
  kind
  sourceInvoiceRef
  intakeChannel
  notes
  createdAt
  createdBy
  vendor { legalName displayName }
  lines { ${AP_INVOICE_LINE_FIELDS} }
`;

const AP_INVOICES_QUERY = `
  query Is($opcoRef: ID!) {
    apInvoices(opcoRef: $opcoRef) {
      ${AP_INVOICE_FIELDS}
    }
  }
`;

export async function fetchApInvoices(
  opcoRef: string,
): Promise<ReadonlyArray<APInvoiceRow>> {
  const { apInvoices } = await graphql<{ apInvoices: APInvoiceRow[] }>(
    AP_INVOICES_QUERY,
    { opcoRef },
  );
  return apInvoices;
}

const AP_INVOICE_QUERY = `
  query I($ref: ID!) {
    apInvoice(ref: $ref) {
      ${AP_INVOICE_FIELDS}
    }
  }
`;

export async function fetchApInvoice(ref: string): Promise<APInvoiceRow | null> {
  const { apInvoice } = await graphql<{ apInvoice: APInvoiceRow | null }>(
    AP_INVOICE_QUERY,
    { ref },
  );
  return apInvoice;
}

const AP_INVOICES_FOR_PO_QUERY = `
  query IsForPo($poRef: ID!) {
    apInvoicesForPo(poRef: $poRef) {
      ${AP_INVOICE_FIELDS}
    }
  }
`;

export async function fetchApInvoicesForPo(
  poRef: string,
): Promise<ReadonlyArray<APInvoiceRow>> {
  const { apInvoicesForPo } = await graphql<{ apInvoicesForPo: APInvoiceRow[] }>(
    AP_INVOICES_FOR_PO_QUERY,
    { poRef },
  );
  return apInvoicesForPo;
}

export interface SubmitAPInvoiceLineInput {
  readonly poLineRef: string;
  readonly description: string;
  readonly quantity: string;
  readonly unitPrice: string;
}

export interface SubmitAPInvoiceInput {
  readonly vendorRef: string;
  readonly opcoRef: string;
  readonly invoiceNumber: string;
  readonly invoiceDate: string;
  readonly currencyCode: string;
  readonly lines: ReadonlyArray<SubmitAPInvoiceLineInput>;
  readonly total?: string;
  readonly intakeChannel: APInvoiceIntakeChannel;
  readonly notes?: string;
  readonly requestingUser: string;
}

export type SubmitAPInvoiceResult =
  | {
      readonly __typename: 'SubmitAPInvoiceSuccess';
      readonly invoiceRef: string;
      readonly invoiceLineRefs: ReadonlyArray<string>;
      readonly newState: APInvoiceState;
    }
  | {
      readonly __typename: 'SubmitAPInvoiceFailure';
      readonly class: string;
      readonly details: unknown;
    };

const SUBMIT_AP_INVOICE_MUTATION = `
  mutation SubmitI($input: SubmitAPInvoiceInput!) {
    submitApInvoice(input: $input) {
      __typename
      ... on SubmitAPInvoiceSuccess { invoiceRef invoiceLineRefs newState }
      ... on SubmitAPInvoiceFailure { class details }
    }
  }
`;

export async function submitApInvoice(
  input: SubmitAPInvoiceInput,
): Promise<SubmitAPInvoiceResult> {
  const { submitApInvoice: result } = await graphql<{
    submitApInvoice: SubmitAPInvoiceResult;
  }>(SUBMIT_AP_INVOICE_MUTATION, { input });
  return result;
}

// §6.3 non-PO invoice (utilities / rent / one-off services). No PO matching,
// every line carries its own GL account.
export interface SubmitNonPOInvoiceLineInput {
  readonly description: string;
  readonly quantity: string;
  readonly unitPrice: string;
  readonly glAccount: string;
}

export interface SubmitNonPOInvoiceInput {
  readonly vendorRef: string;
  readonly opcoRef: string;
  readonly invoiceNumber: string;
  readonly invoiceDate: string;
  readonly currencyCode: string;
  readonly lines: ReadonlyArray<SubmitNonPOInvoiceLineInput>;
  readonly total?: string;
  readonly intakeChannel: APInvoiceIntakeChannel;
  readonly notes?: string;
  readonly requestingUser: string;
}

export type SubmitNonPOInvoiceResult =
  | {
      readonly __typename: 'SubmitNonPOInvoiceSuccess';
      readonly invoiceRef: string;
      readonly invoiceLineRefs: ReadonlyArray<string>;
      readonly newState: APInvoiceState;
    }
  | {
      readonly __typename: 'SubmitNonPOInvoiceFailure';
      readonly class: string;
      readonly details: unknown;
    };

const SUBMIT_NON_PO_INVOICE_MUTATION = `
  mutation SubmitNonPO($input: SubmitNonPOInvoiceInput!) {
    submitNonPOInvoice(input: $input) {
      __typename
      ... on SubmitNonPOInvoiceSuccess { invoiceRef invoiceLineRefs newState }
      ... on SubmitNonPOInvoiceFailure { class details }
    }
  }
`;

export async function submitNonPOInvoice(
  input: SubmitNonPOInvoiceInput,
): Promise<SubmitNonPOInvoiceResult> {
  const { submitNonPOInvoice: result } = await graphql<{
    submitNonPOInvoice: SubmitNonPOInvoiceResult;
  }>(SUBMIT_NON_PO_INVOICE_MUTATION, { input });
  return result;
}

// §6.4 — credit note (vendor credit, standalone or against a source invoice)
export interface SubmitCreditNoteLineInput {
  readonly description: string;
  readonly quantity: string;
  readonly unitPrice: string;
  readonly glAccount: string;
}

export interface SubmitCreditNoteInput {
  readonly vendorRef: string;
  readonly opcoRef: string;
  readonly invoiceNumber: string;
  readonly invoiceDate: string;
  readonly currencyCode: string;
  readonly lines: ReadonlyArray<SubmitCreditNoteLineInput>;
  readonly total?: string;
  readonly intakeChannel: APInvoiceIntakeChannel;
  readonly notes?: string;
  readonly requestingUser: string;
  readonly sourceInvoiceRef?: string;
}

export type SubmitCreditNoteResult =
  | {
      readonly __typename: 'SubmitCreditNoteSuccess';
      readonly invoiceRef: string;
      readonly invoiceLineRefs: ReadonlyArray<string>;
      readonly newState: APInvoiceState;
    }
  | {
      readonly __typename: 'SubmitCreditNoteFailure';
      readonly class: string;
      readonly details: unknown;
    };

const SUBMIT_CREDIT_NOTE_MUTATION = `
  mutation SubmitCN($input: SubmitCreditNoteInput!) {
    submitCreditNote(input: $input) {
      __typename
      ... on SubmitCreditNoteSuccess { invoiceRef invoiceLineRefs newState }
      ... on SubmitCreditNoteFailure { class details }
    }
  }
`;

export async function submitCreditNote(
  input: SubmitCreditNoteInput,
): Promise<SubmitCreditNoteResult> {
  const { submitCreditNote: result } = await graphql<{
    submitCreditNote: SubmitCreditNoteResult;
  }>(SUBMIT_CREDIT_NOTE_MUTATION, { input });
  return result;
}

// ---------------------------------------------------------------------------
// AP invoice state transitions
// ---------------------------------------------------------------------------

// Client-side mirror of the server's adjacency for showing action buttons.
// Server remains the authority — illegal attempts return ILLEGAL_TRANSITION.
// Keep in sync with packages/streams/p2p/src/domain/ap-invoice-state-machine.ts.
const INVOICE_TRANSITIONS: Readonly<Record<APInvoiceState, ReadonlyArray<APInvoiceState>>> = {
  RECEIVED: [],
  MATCHED: ['APPROVED', 'REJECTED'],
  VARIANCE_REVIEW: ['APPROVED', 'REJECTED', 'ON_HOLD'],
  ON_HOLD: ['VARIANCE_REVIEW', 'REJECTED', 'CANCELLED'],
  APPROVED: [],
  REJECTED: [],
  CANCELLED: [],
};

export function legalNextInvoiceStates(from: APInvoiceState): ReadonlyArray<APInvoiceState> {
  return INVOICE_TRANSITIONS[from];
}

export interface ResolveAPInvoiceInput {
  readonly invoiceRef: string;
  readonly toState: APInvoiceState;
  readonly reason?: string;
  readonly requestingUser: string;
}

export type ResolveAPInvoiceResult =
  | {
      readonly __typename: 'ResolveAPInvoiceSuccess';
      readonly invoiceRef: string;
      readonly fromState: APInvoiceState;
      readonly toState: APInvoiceState;
    }
  | {
      readonly __typename: 'ResolveAPInvoiceFailure';
      readonly class: string;
      readonly details: unknown;
    };

const RESOLVE_AP_INVOICE_MUTATION = `
  mutation ResolveI($input: ResolveAPInvoiceInput!) {
    resolveApInvoice(input: $input) {
      __typename
      ... on ResolveAPInvoiceSuccess { invoiceRef fromState toState }
      ... on ResolveAPInvoiceFailure { class details }
    }
  }
`;

export async function resolveApInvoice(
  input: ResolveAPInvoiceInput,
): Promise<ResolveAPInvoiceResult> {
  const { resolveApInvoice: result } = await graphql<{
    resolveApInvoice: ResolveAPInvoiceResult;
  }>(RESOLVE_AP_INVOICE_MUTATION, { input });
  return result;
}

// ---------------------------------------------------------------------------
// PO admin state transitions
// ---------------------------------------------------------------------------

// Receipt-driven edges; admin actions can't reach these via
// updatePurchaseOrderState — server returns ILLEGAL_TRANSITION with reason
// RESERVED_FOR_RECEIPT_FLOW. Keep in sync with
// packages/streams/p2p/src/domain/purchase-order-state-machine.ts.
const PO_RECEIPT_EDGES: ReadonlyArray<readonly [PurchaseOrderState, PurchaseOrderState]> = [
  ['OPEN', 'PARTIALLY_RECEIVED'],
  ['OPEN', 'FULLY_RECEIVED'],
  ['PARTIALLY_RECEIVED', 'PARTIALLY_RECEIVED'],
  ['PARTIALLY_RECEIVED', 'FULLY_RECEIVED'],
];

const PO_TRANSITIONS: Readonly<Record<PurchaseOrderState, ReadonlyArray<PurchaseOrderState>>> = {
  DRAFT: ['APPROVED', 'CANCELLED'],
  APPROVED: ['TRANSMITTED', 'CANCELLED'],
  TRANSMITTED: ['ACKNOWLEDGED', 'OPEN', 'CANCELLED'],
  ACKNOWLEDGED: ['OPEN', 'CANCELLED'],
  OPEN: ['PARTIALLY_RECEIVED', 'FULLY_RECEIVED', 'CANCELLED'],
  PARTIALLY_RECEIVED: ['PARTIALLY_RECEIVED', 'FULLY_RECEIVED', 'CANCELLED'],
  FULLY_RECEIVED: ['CLOSED'],
  CLOSED: [],
  CANCELLED: [],
};

export function adminLegalNextPoStates(
  from: PurchaseOrderState,
): ReadonlyArray<PurchaseOrderState> {
  return PO_TRANSITIONS[from].filter(
    (to) => !PO_RECEIPT_EDGES.some(([f, t]) => f === from && t === to),
  );
}

export interface UpdatePurchaseOrderStateInput {
  readonly poRef: string;
  readonly toState: PurchaseOrderState;
  readonly reason?: string;
  readonly requestingUser: string;
}

export type UpdatePurchaseOrderStateResult =
  | {
      readonly __typename: 'UpdatePurchaseOrderStateSuccess';
      readonly poRef: string;
      readonly fromState: PurchaseOrderState;
      readonly toState: PurchaseOrderState;
    }
  | {
      readonly __typename: 'UpdatePurchaseOrderStateFailure';
      readonly class: string;
      readonly details: unknown;
    };

const UPDATE_PURCHASE_ORDER_STATE_MUTATION = `
  mutation UpdatePO($input: UpdatePurchaseOrderStateInput!) {
    updatePurchaseOrderState(input: $input) {
      __typename
      ... on UpdatePurchaseOrderStateSuccess { poRef fromState toState }
      ... on UpdatePurchaseOrderStateFailure { class details }
    }
  }
`;

export async function updatePurchaseOrderState(
  input: UpdatePurchaseOrderStateInput,
): Promise<UpdatePurchaseOrderStateResult> {
  const { updatePurchaseOrderState: result } = await graphql<{
    updatePurchaseOrderState: UpdatePurchaseOrderStateResult;
  }>(UPDATE_PURCHASE_ORDER_STATE_MUTATION, { input });
  return result;
}

// ---------------------------------------------------------------------------
// Requisitions (§4.2)
// ---------------------------------------------------------------------------

export type RequisitionState =
  | 'SUBMITTED'
  | 'APPROVED'
  | 'REJECTED'
  | 'WITHDRAWN'
  | 'CONVERTED';

export interface RequisitionLineRow {
  readonly requisitionLineRef: string;
  readonly requisitionRef: string;
  readonly lineNumber: number;
  readonly productName: string;
  readonly unitOfMeasure: string;
  readonly quantity: string;
  readonly estimatedUnitPrice: string;
  readonly estimatedLineTotal: string;
  readonly defaultGLAccount: string;
  readonly suggestedVendorRef: string | null;
  readonly notes: string | null;
}

export interface RequisitionRow {
  readonly requisitionRef: string;
  readonly opcoRef: string;
  readonly state: RequisitionState;
  readonly requestingUser: string;
  readonly justification: string | null;
  readonly suggestedVendorRef: string | null;
  readonly estimatedTotal: string;
  readonly currencyCode: string;
  readonly notes: string | null;
  readonly createdAt: string;
  readonly suggestedVendor: { readonly legalName: string; readonly displayName: string } | null;
  readonly lines: ReadonlyArray<RequisitionLineRow>;
}

const REQUISITION_LINE_FIELDS = `
  requisitionLineRef
  requisitionRef
  lineNumber
  productName
  unitOfMeasure
  quantity
  estimatedUnitPrice
  estimatedLineTotal
  defaultGLAccount
  suggestedVendorRef
  notes
`;

const REQUISITION_FIELDS = `
  requisitionRef
  opcoRef
  state
  requestingUser
  justification
  suggestedVendorRef
  estimatedTotal
  currencyCode
  notes
  createdAt
  suggestedVendor { legalName displayName }
  lines { ${REQUISITION_LINE_FIELDS} }
`;

const REQUISITIONS_QUERY = `
  query Rs($opcoRef: ID!) {
    requisitions(opcoRef: $opcoRef) {
      ${REQUISITION_FIELDS}
    }
  }
`;

export async function fetchRequisitions(
  opcoRef: string,
): Promise<ReadonlyArray<RequisitionRow>> {
  const { requisitions } = await graphql<{ requisitions: RequisitionRow[] }>(
    REQUISITIONS_QUERY,
    { opcoRef },
  );
  return requisitions;
}

const REQUISITION_QUERY = `
  query R($ref: ID!) {
    requisition(ref: $ref) {
      ${REQUISITION_FIELDS}
    }
  }
`;

export async function fetchRequisition(ref: string): Promise<RequisitionRow | null> {
  const { requisition } = await graphql<{ requisition: RequisitionRow | null }>(
    REQUISITION_QUERY,
    { ref },
  );
  return requisition;
}

export interface SubmitRequisitionLineInput {
  readonly productName: string;
  readonly unitOfMeasure: string;
  readonly quantity: string;
  readonly estimatedUnitPrice: string;
  readonly defaultGLAccount: string;
  readonly suggestedVendorRef?: string;
  readonly notes?: string;
}

export interface SubmitRequisitionInput {
  readonly opcoRef: string;
  readonly justification?: string;
  readonly suggestedVendorRef?: string;
  readonly currencyCode: string;
  readonly lines: ReadonlyArray<SubmitRequisitionLineInput>;
  readonly notes?: string;
  readonly requestingUser: string;
}

export type SubmitRequisitionResult =
  | {
      readonly __typename: 'SubmitRequisitionSuccess';
      readonly requisitionRef: string;
      readonly requisitionLineRefs: ReadonlyArray<string>;
    }
  | {
      readonly __typename: 'SubmitRequisitionFailure';
      readonly class: string;
      readonly details: unknown;
    };

const SUBMIT_REQUISITION_MUTATION = `
  mutation SubmitR($input: SubmitRequisitionInput!) {
    submitRequisition(input: $input) {
      __typename
      ... on SubmitRequisitionSuccess { requisitionRef requisitionLineRefs }
      ... on SubmitRequisitionFailure { class details }
    }
  }
`;

export async function submitRequisition(
  input: SubmitRequisitionInput,
): Promise<SubmitRequisitionResult> {
  const { submitRequisition: result } = await graphql<{
    submitRequisition: SubmitRequisitionResult;
  }>(SUBMIT_REQUISITION_MUTATION, { input });
  return result;
}

// CONVERTED is set by a future ConvertRequisitionToPO gadget — operators
// cannot reach it via updateRequisitionState (server returns
// ILLEGAL_TRANSITION with reason CONVERSION_RESERVED). Keep in sync with
// packages/streams/p2p/src/domain/requisition-state-machine.ts.
const REQUISITION_CONVERSION_EDGES: ReadonlyArray<readonly [RequisitionState, RequisitionState]> = [
  ['APPROVED', 'CONVERTED'],
];

const REQUISITION_TRANSITIONS: Readonly<Record<RequisitionState, ReadonlyArray<RequisitionState>>> = {
  SUBMITTED: ['APPROVED', 'REJECTED', 'WITHDRAWN'],
  APPROVED: ['CONVERTED', 'WITHDRAWN'],
  REJECTED: [],
  WITHDRAWN: [],
  CONVERTED: [],
};

export function adminLegalNextRequisitionStates(
  from: RequisitionState,
): ReadonlyArray<RequisitionState> {
  return REQUISITION_TRANSITIONS[from].filter(
    (to) => !REQUISITION_CONVERSION_EDGES.some(([f, t]) => f === from && t === to),
  );
}

export interface UpdateRequisitionStateInput {
  readonly requisitionRef: string;
  readonly toState: RequisitionState;
  readonly reason?: string;
  readonly requestingUser: string;
}

export type UpdateRequisitionStateResult =
  | {
      readonly __typename: 'UpdateRequisitionStateSuccess';
      readonly requisitionRef: string;
      readonly fromState: RequisitionState;
      readonly toState: RequisitionState;
    }
  | {
      readonly __typename: 'UpdateRequisitionStateFailure';
      readonly class: string;
      readonly details: unknown;
    };

const UPDATE_REQUISITION_STATE_MUTATION = `
  mutation UpdateR($input: UpdateRequisitionStateInput!) {
    updateRequisitionState(input: $input) {
      __typename
      ... on UpdateRequisitionStateSuccess { requisitionRef fromState toState }
      ... on UpdateRequisitionStateFailure { class details }
    }
  }
`;

export async function updateRequisitionState(
  input: UpdateRequisitionStateInput,
): Promise<UpdateRequisitionStateResult> {
  const { updateRequisitionState: result } = await graphql<{
    updateRequisitionState: UpdateRequisitionStateResult;
  }>(UPDATE_REQUISITION_STATE_MUTATION, { input });
  return result;
}

// ---------------------------------------------------------------------------
// Convert requisition → purchase orders
// ---------------------------------------------------------------------------

export interface ConvertRequisitionToPOInput {
  readonly requisitionRef: string;
  readonly requestingUser: string;
}

export interface ConvertedPurchaseOrder {
  readonly poRef: string;
  readonly vendorRef: string;
  readonly lineRefs: ReadonlyArray<string>;
}

export type ConvertRequisitionToPOResult =
  | {
      readonly __typename: 'ConvertRequisitionToPOSuccess';
      readonly requisitionRef: string;
      readonly purchaseOrders: ReadonlyArray<ConvertedPurchaseOrder>;
    }
  | {
      readonly __typename: 'ConvertRequisitionToPOFailure';
      readonly class: string;
      readonly details: unknown;
    };

const CONVERT_REQUISITION_TO_PO_MUTATION = `
  mutation ConvertR($input: ConvertRequisitionToPOInput!) {
    convertRequisitionToPO(input: $input) {
      __typename
      ... on ConvertRequisitionToPOSuccess {
        requisitionRef
        purchaseOrders { poRef vendorRef lineRefs }
      }
      ... on ConvertRequisitionToPOFailure { class details }
    }
  }
`;

export async function convertRequisitionToPO(
  input: ConvertRequisitionToPOInput,
): Promise<ConvertRequisitionToPOResult> {
  const { convertRequisitionToPO: result } = await graphql<{
    convertRequisitionToPO: ConvertRequisitionToPOResult;
  }>(CONVERT_REQUISITION_TO_PO_MUTATION, { input });
  return result;
}

// ---------------------------------------------------------------------------
// §9.2 1099 reporting — read-only aggregation per tax year
// ---------------------------------------------------------------------------

export interface Vendor1099ReportRow {
  readonly vendor: {
    readonly vendorRef: string;
    readonly legalName: string;
    readonly displayName: string;
    readonly taxId: string;
    readonly addressLine1: string;
    readonly addressLine2: string | null;
    readonly city: string;
    readonly region: string;
    readonly postalCode: string;
    readonly country: string;
    readonly state: VendorState;
  };
  readonly taxYear: number;
  readonly totalPaid: string;
  readonly invoiceCount: number;
  readonly formType: string;
}

const VENDORS_1099_REPORT_QUERY = `
  query R1099($opcoRef: ID!, $taxYear: Int!) {
    vendors1099Report(opcoRef: $opcoRef, taxYear: $taxYear) {
      vendor {
        vendorRef
        legalName
        displayName
        taxId
        addressLine1
        addressLine2
        city
        region
        postalCode
        country
        state
      }
      taxYear
      totalPaid
      invoiceCount
      formType
    }
  }
`;

export async function fetchVendors1099Report(
  opcoRef: string,
  taxYear: number,
): Promise<ReadonlyArray<Vendor1099ReportRow>> {
  const { vendors1099Report } = await graphql<{
    vendors1099Report: Vendor1099ReportRow[];
  }>(VENDORS_1099_REPORT_QUERY, { opcoRef, taxYear });
  return vendors1099Report;
}

// §7.1 / §7.2 — payment runs

export type DiscountPolicy = 'TAKE_WHEN_ECONOMIC' | 'ALWAYS_TAKE' | 'ALWAYS_SKIP';

export type PaymentRunState =
  | 'PROPOSED'
  | 'APPROVED'
  | 'DECLINED'
  | 'EXECUTING'
  | 'EXECUTED'
  | 'PARTIALLY_EXECUTED'
  | 'RECONCILED';

export type PaymentRunPaymentState =
  | 'PROPOSED'
  | 'APPROVED'
  | 'EXCLUDED'
  | 'EXECUTING'
  | 'EXECUTED'
  | 'FAILED'
  | 'CLEARED'
  | 'STOPPED';

export type PaymentRunPaymentMethod = 'ACH' | 'CHECK' | 'WIRE' | 'VIRTUAL_CARD';

export type PaymentInvoiceLinkKind = 'INVOICE_APPLIED' | 'CREDIT_APPLIED';

export interface PaymentApplicationRow {
  readonly invoiceRef: string;
  readonly linkKind: PaymentInvoiceLinkKind;
  readonly appliedAmount: string;
  readonly discountTakenAmount: string | null;
}

export interface PaymentRunPaymentRow {
  readonly paymentRef: string;
  readonly runRef: string;
  readonly vendorRef: string;
  readonly opcoRef: string;
  readonly paymentMethod: PaymentRunPaymentMethod;
  readonly currencyCode: string;
  readonly scheduledDate: string;
  readonly state: PaymentRunPaymentState;
  readonly grossAmount: string;
  readonly discountAmount: string;
  readonly creditAmount: string;
  readonly netAmount: string;
  readonly externalRef: string | null;
  readonly executedAt: string | null;
  readonly clearedAt: string | null;
  readonly failureClass: string | null;
  readonly stopPaymentAt: string | null;
  readonly stopPaymentReason: string | null;
  readonly createdAt: string;
  readonly applications: ReadonlyArray<PaymentApplicationRow>;
}

export interface PaymentRunRow {
  readonly runRef: string;
  readonly opcoRef: string;
  readonly scheduleDate: string;
  readonly lookaheadDays: number;
  readonly currencyCode: string;
  readonly state: PaymentRunState;
  readonly paymentCount: number;
  readonly grossTotal: string;
  readonly discountTotal: string;
  readonly creditTotal: string;
  readonly netTotal: string;
  readonly notes: string | null;
  readonly createdAt: string;
  readonly createdBy: string;
  readonly approvedAt: string | null;
  readonly approvedBy: string | null;
  readonly secondApprovedAt: string | null;
  readonly secondApprovedBy: string | null;
  readonly declinedAt: string | null;
  readonly declinedBy: string | null;
  readonly declinedReason: string | null;
  readonly executionStartedAt: string | null;
  readonly executionCompletedAt: string | null;
  readonly reconciledAt: string | null;
  readonly payments: ReadonlyArray<PaymentRunPaymentRow>;
}

const PAYMENT_RUN_FIELDS = `
  runRef opcoRef scheduleDate lookaheadDays currencyCode state paymentCount
  grossTotal discountTotal creditTotal netTotal notes
  createdAt createdBy approvedAt approvedBy secondApprovedAt secondApprovedBy
  declinedAt declinedBy declinedReason
  executionStartedAt executionCompletedAt reconciledAt
  payments {
    paymentRef runRef vendorRef opcoRef paymentMethod currencyCode scheduledDate
    state grossAmount discountAmount creditAmount netAmount
    externalRef executedAt clearedAt failureClass stopPaymentAt stopPaymentReason
    createdAt
    applications { invoiceRef linkKind appliedAmount discountTakenAmount }
  }
`;

const PAYMENT_RUNS_QUERY = `
  query PaymentRuns($opcoRef: ID!) {
    paymentRuns(opcoRef: $opcoRef) { ${PAYMENT_RUN_FIELDS} }
  }
`;

const PAYMENT_RUN_QUERY = `
  query PaymentRun($runRef: ID!) {
    paymentRun(runRef: $runRef) { ${PAYMENT_RUN_FIELDS} }
  }
`;

export async function fetchPaymentRuns(
  opcoRef: string,
): Promise<ReadonlyArray<PaymentRunRow>> {
  const { paymentRuns } = await graphql<{ paymentRuns: PaymentRunRow[] }>(
    PAYMENT_RUNS_QUERY,
    { opcoRef },
  );
  return paymentRuns;
}

export async function fetchPaymentRun(runRef: string): Promise<PaymentRunRow | null> {
  const { paymentRun } = await graphql<{ paymentRun: PaymentRunRow | null }>(
    PAYMENT_RUN_QUERY,
    { runRef },
  );
  return paymentRun;
}

export interface SchedulePaymentRunInput {
  readonly opcoRef: string;
  readonly scheduleDate: string;
  readonly lookaheadDays: number;
  readonly requestingUser: string;
}

export type SchedulePaymentRunResult =
  | {
      readonly __typename: 'SchedulePaymentRunSuccess';
      readonly runRef: string;
      readonly paymentCount: number;
      readonly grossTotal: string;
      readonly discountTotal: string;
      readonly creditTotal: string;
      readonly netTotal: string;
      readonly currencyCode: string;
    }
  | {
      readonly __typename: 'SchedulePaymentRunFailure';
      readonly class: string;
      readonly details: unknown;
    };

const SCHEDULE_MUTATION = `
  mutation Sched($input: SchedulePaymentRunInput!) {
    schedulePaymentRun(input: $input) {
      __typename
      ... on SchedulePaymentRunSuccess {
        runRef paymentCount grossTotal discountTotal creditTotal netTotal currencyCode
      }
      ... on SchedulePaymentRunFailure { class details }
    }
  }
`;

export async function schedulePaymentRun(
  input: SchedulePaymentRunInput,
): Promise<SchedulePaymentRunResult> {
  const { schedulePaymentRun: result } = await graphql<{
    schedulePaymentRun: SchedulePaymentRunResult;
  }>(SCHEDULE_MUTATION, { input });
  return result;
}

export interface ApprovePaymentRunInput {
  readonly runRef: string;
  readonly requestingUser: string;
}

export type ApprovePaymentRunResult =
  | {
      readonly __typename: 'ApprovePaymentRunSuccess';
      readonly runRef: string;
      readonly state: PaymentRunState;
      readonly requiresSecondApproval: boolean;
    }
  | {
      readonly __typename: 'ApprovePaymentRunFailure';
      readonly class: string;
      readonly details: unknown;
    };

const APPROVE_MUTATION = `
  mutation Approve($input: ApprovePaymentRunInput!) {
    approvePaymentRun(input: $input) {
      __typename
      ... on ApprovePaymentRunSuccess { runRef state requiresSecondApproval }
      ... on ApprovePaymentRunFailure { class details }
    }
  }
`;

export async function approvePaymentRun(
  input: ApprovePaymentRunInput,
): Promise<ApprovePaymentRunResult> {
  const { approvePaymentRun: result } = await graphql<{
    approvePaymentRun: ApprovePaymentRunResult;
  }>(APPROVE_MUTATION, { input });
  return result;
}

export interface DeclinePaymentRunInput {
  readonly runRef: string;
  readonly reason: string;
  readonly requestingUser: string;
}

export type DeclinePaymentRunResult =
  | {
      readonly __typename: 'DeclinePaymentRunSuccess';
      readonly runRef: string;
      readonly state: PaymentRunState;
    }
  | {
      readonly __typename: 'DeclinePaymentRunFailure';
      readonly class: string;
      readonly details: unknown;
    };

const DECLINE_MUTATION = `
  mutation Decline($input: DeclinePaymentRunInput!) {
    declinePaymentRun(input: $input) {
      __typename
      ... on DeclinePaymentRunSuccess { runRef state }
      ... on DeclinePaymentRunFailure { class details }
    }
  }
`;

export async function declinePaymentRun(
  input: DeclinePaymentRunInput,
): Promise<DeclinePaymentRunResult> {
  const { declinePaymentRun: result } = await graphql<{
    declinePaymentRun: DeclinePaymentRunResult;
  }>(DECLINE_MUTATION, { input });
  return result;
}

export interface ExecutePaymentRunInput {
  readonly runRef: string;
  readonly requestingUser: string;
}

export type ExecutePaymentRunResult =
  | {
      readonly __typename: 'ExecutePaymentRunSuccess';
      readonly runRef: string;
      readonly state: PaymentRunState;
      readonly executedPaymentCount: number;
      readonly executedAt: string;
    }
  | {
      readonly __typename: 'ExecutePaymentRunFailure';
      readonly class: string;
      readonly details: unknown;
    };

const EXECUTE_MUTATION = `
  mutation Execute($input: ExecutePaymentRunInput!) {
    executePaymentRun(input: $input) {
      __typename
      ... on ExecutePaymentRunSuccess { runRef state executedPaymentCount executedAt }
      ... on ExecutePaymentRunFailure { class details }
    }
  }
`;

export async function executePaymentRun(
  input: ExecutePaymentRunInput,
): Promise<ExecutePaymentRunResult> {
  const { executePaymentRun: result } = await graphql<{
    executePaymentRun: ExecutePaymentRunResult;
  }>(EXECUTE_MUTATION, { input });
  return result;
}

// §7.1 OPCO config admin

export interface OpcoPaymentConfig {
  readonly opcoRef: string;
  readonly discountPolicy: DiscountPolicy;
  readonly dualApprovalThresholdAmount: string;
  readonly defaultPaymentMethod: PaymentRunPaymentMethod;
  readonly updatedAt: string;
  readonly updatedBy: string;
}

const OPCO_PAYMENT_CONFIG_QUERY = `
  query OpcoPaymentConfig($opcoRef: ID!) {
    opcoPaymentConfig(opcoRef: $opcoRef) {
      opcoRef discountPolicy dualApprovalThresholdAmount defaultPaymentMethod updatedAt updatedBy
    }
  }
`;

export async function fetchOpcoPaymentConfig(
  opcoRef: string,
): Promise<OpcoPaymentConfig | null> {
  const { opcoPaymentConfig } = await graphql<{
    opcoPaymentConfig: OpcoPaymentConfig | null;
  }>(OPCO_PAYMENT_CONFIG_QUERY, { opcoRef });
  return opcoPaymentConfig;
}

export interface SetOpcoPaymentConfigInput {
  readonly opcoRef: string;
  readonly discountPolicy: DiscountPolicy;
  readonly dualApprovalThresholdAmount: string;
  readonly defaultPaymentMethod: PaymentRunPaymentMethod;
  readonly requestingUser: string;
}

export type SetOpcoPaymentConfigResult =
  | {
      readonly __typename: 'SetOpcoPaymentConfigSuccess';
      readonly opcoRef: string;
      readonly changed: boolean;
    }
  | {
      readonly __typename: 'SetOpcoPaymentConfigFailure';
      readonly class: string;
      readonly details: unknown;
    };

const SET_OPCO_CONFIG_MUTATION = `
  mutation SetCfg($input: SetOpcoPaymentConfigInput!) {
    setOpcoPaymentConfig(input: $input) {
      __typename
      ... on SetOpcoPaymentConfigSuccess { opcoRef changed }
      ... on SetOpcoPaymentConfigFailure { class details }
    }
  }
`;

export async function setOpcoPaymentConfig(
  input: SetOpcoPaymentConfigInput,
): Promise<SetOpcoPaymentConfigResult> {
  const { setOpcoPaymentConfig: result } = await graphql<{
    setOpcoPaymentConfig: SetOpcoPaymentConfigResult;
  }>(SET_OPCO_CONFIG_MUTATION, { input });
  return result;
}

export interface MarkPaymentClearedInput {
  readonly paymentRef: string;
  readonly confirmationRef?: string;
  readonly clearedAt?: string;
  readonly requestingUser: string;
}

export type MarkPaymentClearedResult =
  | {
      readonly __typename: 'MarkPaymentClearedSuccess';
      readonly paymentRef: string;
      readonly runRef: string;
      readonly runState: PaymentRunState;
      readonly runReconciled: boolean;
    }
  | {
      readonly __typename: 'MarkPaymentClearedFailure';
      readonly class: string;
      readonly details: unknown;
    };

const MARK_CLEARED_MUTATION = `
  mutation MarkCleared($input: MarkPaymentClearedInput!) {
    markPaymentCleared(input: $input) {
      __typename
      ... on MarkPaymentClearedSuccess { paymentRef runRef runState runReconciled }
      ... on MarkPaymentClearedFailure { class details }
    }
  }
`;

export async function markPaymentCleared(
  input: MarkPaymentClearedInput,
): Promise<MarkPaymentClearedResult> {
  const { markPaymentCleared: result } = await graphql<{
    markPaymentCleared: MarkPaymentClearedResult;
  }>(MARK_CLEARED_MUTATION, { input });
  return result;
}

// §8 reversals

export interface ReverseGoodsReceiptInput {
  readonly receiptRef: string;
  readonly reason: string;
  readonly requestingUser: string;
}

export type ReverseGoodsReceiptResult =
  | {
      readonly __typename: 'ReverseGoodsReceiptSuccess';
      readonly receiptRef: string;
      readonly newPoState: string;
    }
  | {
      readonly __typename: 'ReverseGoodsReceiptFailure';
      readonly class: string;
      readonly details: unknown;
    };

const REVERSE_GOODS_RECEIPT_MUTATION = `
  mutation RevRcpt($input: ReverseGoodsReceiptInput!) {
    reverseGoodsReceipt(input: $input) {
      __typename
      ... on ReverseGoodsReceiptSuccess { receiptRef newPoState }
      ... on ReverseGoodsReceiptFailure { class details }
    }
  }
`;

export async function reverseGoodsReceipt(
  input: ReverseGoodsReceiptInput,
): Promise<ReverseGoodsReceiptResult> {
  const { reverseGoodsReceipt: result } = await graphql<{
    reverseGoodsReceipt: ReverseGoodsReceiptResult;
  }>(REVERSE_GOODS_RECEIPT_MUTATION, { input });
  return result;
}

export interface ReissuePurchaseOrderInput {
  readonly poRef: string;
  readonly notes?: string;
  readonly requestingUser: string;
}

export type ReissuePurchaseOrderResult =
  | {
      readonly __typename: 'ReissuePurchaseOrderSuccess';
      readonly newPoRef: string;
      readonly reissueOfPoRef: string;
      readonly newLineRefs: ReadonlyArray<string>;
    }
  | {
      readonly __typename: 'ReissuePurchaseOrderFailure';
      readonly class: string;
      readonly details: unknown;
    };

const REISSUE_PURCHASE_ORDER_MUTATION = `
  mutation RePO($input: ReissuePurchaseOrderInput!) {
    reissuePurchaseOrder(input: $input) {
      __typename
      ... on ReissuePurchaseOrderSuccess { newPoRef reissueOfPoRef newLineRefs }
      ... on ReissuePurchaseOrderFailure { class details }
    }
  }
`;

export async function reissuePurchaseOrder(
  input: ReissuePurchaseOrderInput,
): Promise<ReissuePurchaseOrderResult> {
  const { reissuePurchaseOrder: result } = await graphql<{
    reissuePurchaseOrder: ReissuePurchaseOrderResult;
  }>(REISSUE_PURCHASE_ORDER_MUTATION, { input });
  return result;
}
